import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { OrganizationAppUserInfo, TeamAppUserStoreInfo } from '@/interfaces/service/api.interface';

interface OrganizationTeamState {
  // === 상태 ===
  // 조직 정보
  organizations: OrganizationAppUserInfo[];

  // 팀 정보
  teams: TeamAppUserStoreInfo[];

  // === 상태 업데이트 액션 ===
  // 조직 정보 업데이트
  setOrganizations: (organizations: OrganizationAppUserInfo[]) => void;

  // 팀 정보 업데이트
  setTeams: (teams: TeamAppUserStoreInfo[]) => void;

  // API 응답으로 전체 스토어 업데이트
  updateOrganizationTeam: (apiResponseData: any) => void;

  // 조직 팀 정보 초기화하여 저장
  setOrganizationTeam: (apiResponseData: any) => void;

  // 스토어 초기화
  clearOrganizationTeam: () => void;

  // === 조직 접근자 ===
  // 조직 ID로 조직 찾기
  getOrganizationById: (id: string) => OrganizationAppUserInfo | undefined;

  // 조직 ID로 조직 특정 필드 접근
  getOrganizationField: <T>(organizationId: string, field: keyof OrganizationAppUserInfo) => T | undefined;

  // 모든 조직 이름 배열 가져오기
  getAllOrganizationNames: () => string[];

  // 조직 상태별로 그룹화하여 가져오기
  getOrganizationsByStatus: (status: string) => OrganizationAppUserInfo[];

  // 전체 팀 수 가져오기
  getTotalTeamCount: () => number;

  // === 팀 접근자 ===
  // 팀 ID로 팀 찾기
  getTeamById: (id: string) => TeamAppUserStoreInfo | undefined;

  // 팀 ID로 팀 특정 필드 접근
  getTeamField: <T>(teamId: string, field: keyof TeamAppUserStoreInfo) => T | undefined;

  // 병원 ID로 팀 찾기
  getTeamsByStoreId: (storeId: number) => TeamAppUserStoreInfo[];

  // 모든 팀 이름 배열 가져오기
  getAllTeamNames: () => string[];

  // 권한별로 팀 필터링 (읽기/쓰기 권한)
  getTeamsByPermission: (hasWriting: boolean, hasReading: boolean) => TeamAppUserStoreInfo[];
}

export const useOrganizationTeamStore = create<OrganizationTeamState>()(
  devtools(
    persist(
      (set, get) => ({
        // === 초기 상태 ===
        organizations: [],
        teams: [],

        // === 상태 업데이트 함수 ===
        setOrganizations: (organizations) => set({ organizations }),
        setTeams: (teams) => set({ teams }),

        setOrganizationTeam: (apiResponseData) => {
          set({
            organizations: apiResponseData.organizationAppUserInfo || [],
            teams: apiResponseData.organizationAppUserInfo.flatMap((org) => org.teamAppUserStoresInfo || [])
          });
        },

        updateOrganizationTeam: (apiResponseData) => {
          // 비구조화 할당으로 데이터 추출
          const { organizationAppUserInfo } = apiResponseData || {};

          // 모든 팀 정보 추출
          const allTeams: TeamAppUserStoreInfo[] = [];
          if (organizationAppUserInfo && organizationAppUserInfo.length > 0) {
            organizationAppUserInfo.forEach((organization: OrganizationAppUserInfo) => {
              if (organization.teamAppUserStoresInfo && organization.teamAppUserStoresInfo.length > 0) {
                allTeams.push(...organization.teamAppUserStoresInfo);
              }
            });
          }

          // 상태 업데이트
          set({
            organizations: organizationAppUserInfo || [],
            teams: allTeams
          });
        },

        clearOrganizationTeam: () => {
          // sessionStorage에서도 데이터 삭제
          sessionStorage.removeItem('organization-team-storage');
          set({
            organizations: [],
            teams: []
          });
        },

        // === 조직 접근자 ===
        getOrganizationById: (id) => {
          return get().organizations.find((organization) => organization.organizationId === id);
        },

        getOrganizationField: <T>(organizationId: string, field: keyof OrganizationAppUserInfo): T | undefined => {
          const organization = get().organizations.find((item) => item.organizationId === organizationId);
          return organization ? (organization[field] as unknown as T) : undefined;
        },

        getAllOrganizationNames: () => {
          return get().organizations.map((organization) => organization.organizationName);
        },

        getOrganizationsByStatus: (status) => {
          return get().organizations.filter((organization) => organization.status === status);
        },

        getTotalTeamCount: () => {
          return get().teams.length;
        },

        // === 팀 접근자 ===
        getTeamById: (id) => {
          return get().teams.find((storeTeam) => storeTeam.teamId === id);
        },

        getTeamField: <T>(teamId: string, field: keyof TeamAppUserStoreInfo): T | undefined => {
          const storeTeam = get().teams.find((item) => item.teamId === teamId);
          return storeTeam ? (storeTeam[field] as unknown as T) : undefined;
        },

        getTeamsByStoreId: (storeId) => {
          return get().teams.filter((storeTeam) => storeTeam.storeId === storeId);
        },

        getAllTeamNames: () => {
          return get().teams.map((storeTeam) => storeTeam.storeName);
        },

        getTeamsByPermission: (hasWriting, hasReading) => {
          return get().teams.filter((storeTeam) => storeTeam.permissionWriting === hasWriting && storeTeam.permissionReading === hasReading);
        }
      }),
      {
        name: 'organization-team-storage',
        storage: createJSONStorage(() => sessionStorage)
      }
    ),
    {
      name: 'organization-team-store'
    }
  )
);
